import {assertAndContinue} from 'Asserts';
import {ManagedModelType, getModelManager} from 'models/ManagedModelDefinitions';
import {entityIsContainable} from 'samples/containables/ContainableUtil';
import APIUtil from 'util/APIUtil';
import {ValidateItemsResponse} from 'worklists/WorklistConstants';

/**
 * Validates whether a set of items in a worklist can be used in a transfer table. SelectWorklistModal
 * calls this function after a worklist is picked from the search.
 *
 * Batches are assumed to be always valid. Containers are accepted as long as they only have one item
 * per container. Bioentities are valid as long as they have a schema that indicates they are containable.
 */
export default async function validateWorklistItemsTransferable(
  itemIds: Array<string>
): Promise<ValidateItemsResponse> {
  assertAndContinue(
    itemIds.every((id) => APIUtil.isContainer(id) || APIUtil.isBiologicalEntity(id) || APIUtil.isBatch(id)),
    'Expected ids passed from worklist to be bioentity, batch, or container ids only.'
  );

  // Batches need no extra validation, so we only check containers or bioentities
  const containerIds = itemIds.filter((id) => APIUtil.isContainer(id));
  const bioentityIds = itemIds.filter((id) => APIUtil.isBiologicalEntity(id));

  if (bioentityIds.length > 0) {
    const bioentityManager = getModelManager(ManagedModelType.BIOENTITY_SUMMARY);
    const bioentities = await bioentityManager.fetchModelList(bioentityIds);
    const uncontainableEntityIds = bioentities.filter((b) => !entityIsContainable(b)).map((b) => b.id);
    if (uncontainableEntityIds.length > 0) {
      return {
        invalidItemIds: uncontainableEntityIds,
        errorMessage:
          'Some entities in the selected worklist cannot be placed in containers. ' +
          'Please select another worklist, or remove the uncontainable entities from this worklist.',
      };
    }
  }

  if (containerIds.length > 0) {
    const containerManager = getModelManager(ManagedModelType.CONTAINER);
    const containers = await containerManager.fetchModelList(containerIds);
    const containersWithMultipleItemsIds = containers.filter((c) => c.contents.length > 1).map((c) => c.id);
    if (containersWithMultipleItemsIds.length > 0) {
      return {
        invalidItemIds: containersWithMultipleItemsIds,
        errorMessage:
          'Some containers in the selected worklist contain multiple batches or entities, ' +
          'which is currently not supported for filling to transfer tables. ' +
          'Please select another worklist, or remove the containers with multiple batches or entities ' +
          'from this worklist.',
      };
    }
  }
  return {invalidItemIds: [], errorMessage: null};
}
